import { Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CentrexUserModel } from 'src/app/models/centrex-user.model';
import { HistoriqueComponent } from '../../historique/historique.component';
import { MatCardModule } from '@angular/material/card';
import {
  EmailValidator,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValueChangeEvent,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CentrexUsersService } from 'src/app/services/centrex-users-service';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-centrex-users-form',
  templateUrl: './centrex-users-form.component.html',
  styleUrl: './centrex-users-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonModule,
    HistoriqueComponent,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSlideToggle,
    NgxSpinnerModule,
  ],
})
export class CentrexUsersFormComponent implements OnInit {
  form!: FormGroup;
  private spinner = inject(NgxSpinnerService);
  private toastr = inject(ToastrService);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      mode: 'view' | 'edit';
      user: CentrexUserModel;
      firstName?: string;
      lastName?: string;
      email?: string;
    },
    private dialogRef: MatDialogRef<CentrexUsersFormComponent>,
    private centrexUsersService: CentrexUsersService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    const isViewMode = this.data.mode === 'view';

    this.form = this.fb.group({
      lastName: [
        {
          value:
            this.data.user.lastname ??
            this.data.lastName ??
            this.data.user.name?.split(' ')[0] ??
            '',
          disabled: isViewMode,
        },
      ],
      firstName: [
        {
          value:
            this.data.user.firstname ??
            this.data.firstName ??
            this.data.user.name?.split(' ')[1] ??
            '',
          disabled: isViewMode,
        },
      ],
      email: [
        {
          value: this.data.user.email ?? this.data.email ?? '',
          disabled: isViewMode,
        },
      ],
      enabled: [
        { value: this.data.user.enabled ?? false, disabled: isViewMode },
      ],
    });
  }

  /** Récupère le nom depuis le champ `name` formaté "Nom Prénom" */
  getNom(): string {
    return this.data.lastName || this.data.user.name?.split(' ')[0] || '';
  }

  getPrenom(): string {
    return this.data.firstName || this.data.user.name?.split(' ')[1] || '';
  }

  getEmail(): string {
    return this.data.email || this.data.user.email || '—';
  }

  /** Détermine si l'utilisateur est bloqué */
  isBlocked(): boolean {
    return (
      this.data.user.enabled === false || this.data.user.enabled === undefined
    );
  }

  /** Ferme la modale */
  close(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    this.spinner.show('spinner');

    const form_values = { ...this.form.value };
    const updated: CentrexUserModel = {
      ...this.data.user,
      lastname: form_values.lastName,
      firstname: form_values.firstName,
      name: `${form_values.firstName} ${form_values.lastName}`,
      email: form_values.email,
      enabled: form_values.enabled,
      //password: form_values.password,
    };

    const orgId = this.data.user.organizationId;
    const platform = sessionStorage.getItem('platform') ?? 'Centrex Enreach';

    let user_id = '';
    if (platform === 'Centrex NG') {
      user_id = this.data.user.id;
    } else {
      user_id = this.data.user.account;
    }

    this.centrexUsersService
      .updateCentrexUser(user_id, updated, orgId, platform)
      .subscribe({
        next: () => {
          this.spinner.hide('spinner');

          this.toastr.success('Utilisateur modifié avec succès !');

          this.dialogRef.close('updated');
        },
        error: () => {
          this.spinner.hide('spinner');

          this.toastr.error("Échec de la modification de l'utilisateur");
        },
      });
  }
}
